import {Plugins, withSystem, EnvTypes} from '@packages/systems/wfdl';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  toBooleanTag,
  TPredicate,
} from '@packages/systems/plugins-utils/Boolean';
import {TTypeConstructor} from '@packages/systems/wfdl-ast';
import {LINK_TYPES} from '@packages/shared/render/plugins/Basic/dataTypes/Link/LinkTypes';

const LinkType = TTypeConstructor(
  qualify(Plugins.name('Basic'), typeName('Link'))
);

const isValidMode = (mode: string): mode is LINK_TYPES => {
  return Object.values(LINK_TYPES).includes(mode as LINK_TYPES);
};

const isLinkSet = (link: unknown): boolean => {
  if (!link || typeof link !== 'object') {
    return false;
  }

  if (!('mode' in link) || typeof link.mode !== 'string') {
    return false;
  }

  const mode = link.mode;

  if (!isValidMode(mode)) {
    return false;
  }

  switch (mode) {
    case 'external': {
      const url =
        'url' in link && typeof link.url === 'string' ? link.url : undefined;
      return url !== undefined && url !== '' && url !== '#';
    }
    case 'page': {
      const pageId =
        'pageId' in link && typeof link.pageId === 'string'
          ? link.pageId
          : undefined;
      return pageId !== undefined && pageId !== '';
    }
    case 'dynpage': {
      // these are available through dynamoFullSlugToBasicLink bindings
      const pageSlug =
        'pageSlug' in link && typeof link.pageSlug === 'string'
          ? link.pageSlug
          : undefined;
      // or, for a static (collectionId, itemId) pick made outside a bindable context
      const itemId =
        'itemId' in link && typeof link.itemId === 'string'
          ? link.itemId
          : undefined;
      return (
        (pageSlug !== undefined && pageSlug !== '') ||
        (itemId !== undefined && itemId !== '')
      );
    }
    case 'section': {
      const domId =
        'domId' in link && typeof link.domId === 'string'
          ? link.domId
          : undefined;
      return domId !== undefined && domId !== '';
    }
    case 'email': {
      const email =
        'email' in link && typeof link.email === 'string'
          ? link.email
          : undefined;
      const subject =
        'subject' in link && typeof link.subject === 'string'
          ? link.subject
          : undefined;
      return (
        (email !== undefined && email !== '') ||
        (subject !== undefined && subject !== '')
      );
    }
    case 'tel': {
      const tel =
        'tel' in link && typeof link.tel === 'string' ? link.tel : undefined;
      return tel !== undefined && tel !== '';
    }
    case 'file': {
      const assetId =
        'assetId' in link && typeof link.assetId === 'string'
          ? link.assetId
          : undefined;
      if (assetId !== undefined && assetId !== '') {
        return true;
      }
      // these are available through dynamoFileRefToBasicLink bindings
      const assetUrl =
        'assetUrl' in link && typeof link.assetUrl === 'string'
          ? link.assetUrl
          : undefined;
      return assetUrl !== undefined && assetUrl !== '';
    }
    case 'popover': {
      // evaluated targets are either a target id or a resolver function
      const popoverTarget =
        'popoverTarget' in link ? link.popoverTarget : undefined;
      return (
        (typeof popoverTarget === 'string' && popoverTarget !== '') ||
        typeof popoverTarget === 'function'
      );
    }
    case 'modal': {
      const modalTarget = 'modalTarget' in link ? link.modalTarget : undefined;
      return (
        (typeof modalTarget === 'string' && modalTarget !== '') ||
        typeof modalTarget === 'function'
      );
    }
    default: {
      const _exhaustiveCheck: never = mode;
      return false;
    }
  }
};

export const linkIsSet: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: withSystem({label: 'is set'}, TPredicate(LinkType)),
    exported: 'visible',
  },
  value: (link: unknown) => {
    return toBooleanTag(isLinkSet(link));
  },
};

export const linkIsNotSet: EnvTypes.UnsafeTypedValue = {
  envType: {
    dataType: withSystem({label: 'is not set'}, TPredicate(LinkType)),
    exported: 'visible',
  },
  value: (link: unknown) => {
    return toBooleanTag(!isLinkSet(link));
  },
};
